/// Four separate digit boxes backed by one hidden input (autofill-friendly).
/// Port of mobile/lib/ui/pixel/pixel_otp_field.dart.

import { useEffect, useRef } from 'react';

import { PixelContainer } from '@/ui/pixel/PixelContainer';

export interface PixelOtpFieldProps {
  length?: number;
  value: string;
  enabled?: boolean;
  autoFocus?: boolean;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  color: string;
  surface: string;
  surfaceContainer: string;
}

export function PixelOtpField({
  length = 4,
  value,
  enabled = true,
  autoFocus = false,
  onChange,
  onComplete,
  color,
  surfaceContainer,
}: PixelOtpFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const digits = value.padEnd(length, ' ').slice(0, length);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const handleChange = (raw: string): void => {
    const next = raw.replace(/\D/g, '').slice(0, length);
    onChange(next);
    if (next.length === length) onComplete?.(next);
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={length}
        value={value}
        disabled={!enabled}
        onChange={(event) => handleChange(event.target.value)}
        aria-label="Verification code"
        className="absolute inset-0 z-10 cursor-text opacity-[0.01]"
        style={{ fontSize: 28, letterSpacing: '1.5em', border: 'none', background: 'transparent' }}
      />
      <div className="pointer-events-none flex gap-2.5">
        {Array.from({ length }, (_, i) => {
          const ch = digits[i]?.trim() ?? '';
          return (
            <PixelContainer
              key={i}
              radius={3}
              pixelScale={2}
              padding="14px 0"
              backgroundColor={surfaceContainer}
              borderColor={color}
              className="min-w-0 flex-1"
            >
              <div className="text-center leading-none" style={{ fontSize: 28, color, minHeight: 28 }}>
                {ch}
              </div>
            </PixelContainer>
          );
        })}
      </div>
    </div>
  );
}
